import React, { useState, useEffect, useRef } from 'react';
import { userAPI, chatAPI } from '../services/api';
import './Chat.css';

const Chat = ({ user }) => {
  const [activeChat, setActiveChat] = useState('group');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch users from backend API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const response = await userAPI.getUsers();
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        // Fallback to mock data if API fails
        const mockUsers = [
          { id: 1, name: 'John Doe', username: 'john', status: 'online' },
          { id: 2, name: 'Jane Smith', username: 'jane', status: 'offline' },
          { id: 3, name: 'Mike Johnson', username: 'mike', status: 'online' },
        ];
        setUsers(mockUsers);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Fetch chat rooms for current user
  useEffect(() => {
    const fetchChatRooms = async () => {
      try {
        const response = await chatAPI.getChatRooms();
        setChatRooms(response.data);
      } catch (error) {
        console.error('Failed to fetch chat rooms:', error);
      }
    };

    if (user?.id) {
      fetchChatRooms();
    }
  }, [user]);

  // Fetch messages when active chat changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (!user?.id) return;
      
      try {
        setMessagesLoading(true);
        let response;
        
        if (activeChat === 'group') {
          // Fetch group chat messages
          const groupRoom = chatRooms.find(room => room.room_type === 'group');
          if (groupRoom) {
            response = await chatAPI.getRoomMessages(groupRoom.id);
          } else {
            // Try to get or create group chat
            const groupResponse = await chatAPI.getOrCreateGroupChat();
            if (groupResponse.data) {
              setChatRooms(prev => [...prev, groupResponse.data]);
              response = await chatAPI.getRoomMessages(groupResponse.data.id);
            }
          }
        } else {
          // Handle private chat
          const chatUserId = parseInt(activeChat);
          if (isNaN(chatUserId)) {
            setMessages([]);
            return;
          }
          
          // Try to find existing private chat room
          let existingRoom = chatRooms.find(room => 
            room.room_type === 'private' && 
            room.participants.some(p => p.id === chatUserId)
          );
          
          if (existingRoom) {
            response = await chatAPI.getRoomMessages(existingRoom.id);
          } else {
            // Create new private chat room if it doesn't exist
            try {
              const newRoomResponse = await chatAPI.createChatRoom({
                room_type: 'private',
                participants: [user.id, chatUserId]
              });
              existingRoom = newRoomResponse.data;
              setChatRooms(prev => [...prev, existingRoom]);
              response = await chatAPI.getRoomMessages(existingRoom.id);
            } catch (createError) {
              console.error('Failed to create private chat room:', createError);
              setMessages([]);
              return;
            }
          }
        }
        
        setMessages(response?.data || []);
      } catch (error) {
        console.error('Failed to fetch messages:', error);
        setMessages([]);
      } finally {
        setMessagesLoading(false);
      }
    };

    fetchMessages();
  }, [activeChat, user, chatRooms]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user?.id) return;

    // Find the appropriate chat room
    let targetRoom;
    if (activeChat === 'group') {
      targetRoom = chatRooms.find(room => room.room_type === 'group');
      if (!targetRoom) {
        try {
          const groupResponse = await chatAPI.getOrCreateGroupChat();
          targetRoom = groupResponse.data;
          setChatRooms(prev => [...prev, targetRoom]);
        } catch (error) {
          console.error('Failed to get group chat:', error);
          return;
        }
      }
    } else {
      const chatUserId = parseInt(activeChat);
      if (isNaN(chatUserId)) return;
      
      targetRoom = chatRooms.find(room => 
        room.room_type === 'private' && 
        room.participants.some(p => p.id === chatUserId)
      );
      
      if (!targetRoom) {
        try {
          const newRoomResponse = await chatAPI.createChatRoom({
            room_type: 'private',
            participants: [user.id, chatUserId]
          });
          targetRoom = newRoomResponse.data;
          setChatRooms(prev => [...prev, targetRoom]);
        } catch (error) {
          console.error('Failed to create private chat room:', error);
          return;
        }
      }
    }

    const messageData = {
      room: targetRoom.id,
      sender: user.id,
      message: newMessage
    };

    // Optimistically add message to UI
    const optimisticMessage = {
      id: Date.now(),
      message: newMessage,
      sender: {
        id: user.id,
        name: user.name || user.username,
        username: user.username
      },
      sender_id: user.id,
      created_at: new Date().toISOString(),
      room: {
        id: targetRoom.id,
        room_type: targetRoom.room_type,
        participants: targetRoom.participants
      },
      is_deleted: false
    };
    
    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');

    try {
      await chatAPI.sendMessage(messageData);
      
      // Refresh messages after sending
      // In a real app, you'd use WebSocket for real-time updates
      setTimeout(async () => {
        try {
          const response = await chatAPI.getRoomMessages(targetRoom.id);
          setMessages(response.data || []);
        } catch (refreshError) {
          console.error('Failed to refresh messages:', refreshError);
        }
      }, 500);
      
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      setNewMessage(newMessage); // Restore message input
    }
  };

  const handleDeleteMessage = async (messageId) => {
    // Optimistically remove message
    const originalMessages = messages;
    setMessages(prev => prev.map(msg => 
      msg.id === messageId ? { ...msg, is_deleted: true } : msg
    ));
    
    try {
      await chatAPI.deleteMessage(messageId);
    } catch (error) {
      console.error('Failed to delete message:', error);
      // Restore messages on error
      setMessages(originalMessages);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const getFilteredMessages = () => {
    if (activeChat === 'group') {
      return messages.filter(msg => 
        msg.room?.room_type === 'group' && !msg.is_deleted
      );
    } else {
      const chatUserId = parseInt(activeChat);
      if (isNaN(chatUserId)) return [];
      
      return messages.filter(msg => 
        msg.room?.room_type === 'private' && 
        msg.room?.participants?.some(p => p.id === chatUserId) &&
        !msg.is_deleted
      );
    }
  };

  const getActiveChatName = () => {
    if (activeChat === 'group') {
      return 'Group Chat';
    } else {
      const chatUserId = parseInt(activeChat);
      if (isNaN(chatUserId)) return 'Unknown Chat';
      
      const chatUser = users.find(u => u.id === chatUserId);
      return chatUser ? (chatUser.name || chatUser.username || 'Unknown User') : 'Unknown User';
    }
  };

  // Filter users for private chat (exclude current user for private chats)
  const getPrivateChatUsers = () => {
    return users.filter(u => u.id !== user?.id);
  };

  // Get user status from chat status or fallback to user status
  const getUserStatus = (userId) => {
    const userObj = users.find(u => u.id === userId);
    // You can extend this to check ChatStatus model if implemented
    return userObj?.chat_status?.status || userObj?.status || 'offline';
  };

  // Safe placeholder text
  const getPlaceholderText = () => {
    const chatName = getActiveChatName();
    return `Message ${chatName ? chatName.toLowerCase() : 'chat'}...`;
  };

  // Get existing private chat rooms for sidebar
  const getPrivateChatRooms = () => {
    return chatRooms.filter(room => 
      room.room_type === 'private' && 
      room.participants.some(p => p.id !== user?.id)
    );
  };

  // Get group chat room
  const getGroupChatRoom = () => {
    return chatRooms.find(room => room.room_type === 'group');
  };

  return (
    <div className="chat-container">
      {/* Chat Sidebar */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <h3>Chats</h3>
        </div>
        
        <div className="chat-list">
          {/* Group Chat */}
          <div 
            className={`chat-item ${activeChat === 'group' ? 'active' : ''}`}
            onClick={() => setActiveChat('group')}
          >
            <div className="chat-avatar group-avatar">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="chat-info">
              <div className="chat-name">Group Chat</div>
              <div className="chat-preview">Team discussion</div>
            </div>
          </div>

          {/* Existing Private Chat Rooms */}
          {getPrivateChatRooms().map(room => {
            const otherParticipant = room.participants.find(p => p.id !== user?.id);
            if (!otherParticipant) return null;
            
            return (
              <div 
                key={room.id}
                className={`chat-item ${activeChat === otherParticipant.id.toString() ? 'active' : ''}`}
                onClick={() => setActiveChat(otherParticipant.id.toString())}
              >
                <div className="chat-avatar">
                  <div className="avatar-circle">
                    {(otherParticipant.name || otherParticipant.username || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className={`status-indicator ${getUserStatus(otherParticipant.id)}`}></div>
                </div>
                <div className="chat-info">
                  <div className="chat-name">
                    {otherParticipant.name || otherParticipant.username || 'Unknown User'}
                  </div>
                  {room.last_message && (
                    <div className="chat-preview">
                      {room.last_message.message.length > 30 
                        ? room.last_message.message.substring(0, 30) + '...'
                        : room.last_message.message
                      }
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Available Users for New Private Chats */}
          {usersLoading ? (
            <div className="loading-users">Loading users...</div>
          ) : (
            getPrivateChatUsers()
              .filter(chatUser => {
                // Only show users who don't already have a private chat room
                return !getPrivateChatRooms().some(room => 
                  room.participants.some(p => p.id === chatUser.id)
                );
              })
              .map(chatUser => (
                <div 
                  key={chatUser.id}
                  className={`chat-item ${activeChat === chatUser.id.toString() ? 'active' : ''}`}
                  onClick={() => setActiveChat(chatUser.id.toString())}
                >
                  <div className="chat-avatar">
                    <div className="avatar-circle">
                      {(chatUser.name || chatUser.username || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className={`status-indicator ${getUserStatus(chatUser.id)}`}></div>
                  </div>
                  <div className="chat-info">
                    <div className="chat-name">{chatUser.name || chatUser.username || 'Unknown User'}</div>
                    <div className="chat-preview">Start a conversation</div>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Chat Main Area */}
      <div className="chat-main">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <h3>{getActiveChatName()}</h3>
            {activeChat !== 'group' && (
              <span className="chat-status">
                {getUserStatus(parseInt(activeChat))}
              </span>
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="messages-container">
          {messagesLoading ? (
            <div className="loading-messages">Loading messages...</div>
          ) : (
            <>
              {getFilteredMessages().map((message, index) => {
                const prevMessage = getFilteredMessages()[index - 1];
                const showDate = !prevMessage || 
                  formatDate(message.created_at) !== formatDate(prevMessage.created_at);
                
                return (
                  <div key={message.id}>
                    {showDate && (
                      <div className="date-separator">
                        {formatDate(message.created_at)}
                      </div>
                    )}
                    
                    <div className={`message ${message.sender?.id === user?.id || message.sender_id === user?.id ? 'own' : 'other'}`}>
                      <div className="message-content">
                        {(message.sender?.id !== user?.id && message.sender_id !== user?.id) && activeChat === 'group' && (
                          <div className="message-sender">
                            {message.sender?.name || message.sender?.username || 'Unknown'}
                          </div>
                        )}
                        <div className="message-text">{message.message}</div>
                        <div className="message-meta">
                          <span className="message-time">{formatTime(message.created_at)}</span>
                          {(message.sender?.id === user?.id || message.sender_id === user?.id) && (
                            <button 
                              className="delete-message-btn"
                              onClick={() => handleDeleteMessage(message.id)}
                              title="Delete message"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Message Input */}
        <div className="message-input-container">
          <div className="message-input-wrapper">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={getPlaceholderText()}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              className="message-input"
              disabled={!user?.id}
            />
            <button 
              onClick={handleSendMessage}
              className="send-button"
              disabled={!newMessage.trim() || !user?.id}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="22" y1="2" x2="11" y2="13" stroke="currentColor" strokeWidth="2"/>
                <polygon points="22,2 15,22 11,13 2,9 22,2" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;